import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, CircularProgress, Alert } from '@mui/material';
import { useTheme } from '@mui/material';
import { useRecipeNotes } from '../../hooks/useRecipeNotes.js';

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const RecipeNotes = ({ recipeId, userId }) => {
  const theme = useTheme();
  const { note, setNote, saveNote, loading, error, isSaving } = useRecipeNotes(userId, recipeId);
  const [localNote, setLocalNote] = useState('');
  
  // Initialize local note state from the fetched note
  useEffect(() => {
    setLocalNote(note);
  }, [note]);
  
  // Create a debounced save function
  const debouncedSave = useCallback(
    debounce((noteText) => {
      saveNote(noteText);
    }, 800),
    [saveNote]
  );
  
  // Handle note changes
  const handleNoteChange = (e) => {
    const newValue = e.target.value;
    setLocalNote(newValue);
    debouncedSave(newValue);
  };
  
  // Explicitly save on blur
  const handleBlur = () => {
    saveNote(localNote);
  };

  return (
    <Box 
      sx={{ 
        mt: 3, 
        mb: 3, 
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
        p: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
          My Notes
        </Typography>
        {isSaving && (
          <CircularProgress size={20} thickness={5} sx={{ ml: 1 }} />
        )}
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TextField
          multiline
          fullWidth
          minRows={3}
          maxRows={10}
          variant="outlined"
          placeholder="Add your personal notes about this recipe here..."
          value={localNote}
          onChange={handleNoteChange}
          onBlur={handleBlur}
          disabled={isSaving}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.02)'
            }
          }}
        />
      )}
    </Box>
  );
};

export default RecipeNotes; 